/**
 * Lamp Builder
 *
 * Flow: Model → Color → Size → Add-ons → Summary.
 * Product-driven: each model lists its color-product handles. Colors, sizes,
 * prices and images are read live from those products (/products/HANDLE.js):
 *   • model price  = smallest-size price ("från …")
 *   • color        = one product per color (price unchanged)
 *   • size         = product variant — sets the exact price
 * A live preview image + price follows every step and always shows the latest
 * choice. Accessories are added to the cart as an included bundle.
 */

(function () {
  'use strict';

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.from((r || document).querySelectorAll(s)); }
  function esc(str) { const d = document.createElement('div'); d.textContent = str == null ? '' : String(str); return d.innerHTML; }
  function toInt(v) { const n = parseInt(v, 10); return isNaN(n) ? null : n; }
  function money(cents) {
    if (cents == null) return '';
    return (cents / 100).toLocaleString('sv-SE', { maximumFractionDigits: 0 }) + ' kr';
  }
  function signedMoney(diff) {
    if (diff === 0) return '±0 kr';
    return (diff > 0 ? '+' : '−') + money(Math.abs(diff));
  }
  function plusMoney(diff) {
    // always shows a leading + (e.g. "+0 kr", "+1 000 kr")
    return '+' + money(diff < 0 ? 0 : diff);
  }
  function parseDims(text) {
    // "Liten=Ø 32 cm · höjd 24 cm" lines (newline or ; separated) -> { liten: "..." }
    const map = {};
    (text || '').split(/[\n;]+/).forEach(function (line) {
      const i = line.indexOf('=');
      if (i === -1) return;
      const key = line.slice(0, i).trim().toLowerCase();
      const val = line.slice(i + 1).trim();
      if (key) map[key] = val;
    });
    return map;
  }
  async function fetchProduct(handle) {
    if (!handle) return null;
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
      if (!res.ok) return null;
      return await res.json();
    } catch (e) { console.warn('[lamp-builder] product load failed:', handle, e); return null; }
  }

  const CHECK = '<span class="lamp-builder__check"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>';
  const PH = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>';

  // Color name → swatch colour (used for the little colour buttons under the image)
  const COLOR_HEX = {
    'ljusbrun': '#c9a87c', 'brun': '#8a5a2b', 'mörkbrun': '#5c3a1e', 'morkbrun': '#5c3a1e',
    'ljusgrön': '#a7b88a', 'ljusgron': '#a7b88a', 'grön': '#6f8f5a', 'gron': '#6f8f5a',
    'orange': '#c8641e', 'svart': '#2f2d2a', 'vit': '#efe9dd', 'natur': '#d8c4a0',
    'grå': '#9a968d', 'gra': '#9a968d', 'beige': '#d9c9ad'
  };
  function colorHex(label) { return COLOR_HEX[(label || '').toLowerCase().trim()] || '#c9b59a'; }

  class LampBuilder {
    constructor(el) {
      this.el = el;
      this.id = el.dataset.sectionId;
      const cfgEl = document.getElementById('lamp-builder-config-' + this.id);
      this.cfg = cfgEl ? JSON.parse(cfgEl.textContent) : null;
      if (!this.cfg) return;

      // Normalise model colorHandles (trim)
      (this.cfg.models || []).forEach(function (m) {
        m.colorHandles = (m.colorHandles || []).map(function (h) { return (h || '').trim(); }).filter(Boolean);
      });

      this.currentStep = 1;
      this.cache = {}; // handle -> product json
      this.s = { modelIndex: null, colorIndex: null, sizeIndex: null, cable: null, bulb: null, socket: null, engraving: false, engravingText: '', trees: 0, deliveryDate: '', quantity: 1 };

      // group accessories
      this.groups = { cable: [], bulb: [], socket: [] };
      (this.cfg.accessories || []).forEach(function (a) {
        if (!this.groups[a.group]) this.groups[a.group] = [];
        this.groups[a.group].push(a);
      }, this);

      this.bind();
      this.el.dataset.step = '1';
      this.openGroup = 'cable';
      this.renderModels();
      this.renderAccessories();
      this.setupAccordions();
      this.setupExtras();
      this.setupDelivery();
      this.setupQuantity();
      this.updatePreview();
      this.prefetchAll();
      this.prefetchAccessoryImages();
    }

    bind() {
      $$('[data-lb-next]', this.el).forEach(function (b) { b.addEventListener('click', this.next.bind(this)); }, this);
      $$('[data-lb-prev]', this.el).forEach(function (b) { b.addEventListener('click', this.prev.bind(this)); }, this);
      $$('[data-lb-goto]', this.el).forEach(function (b) {
        b.addEventListener('click', function () { if (!b.disabled) this.goTo(toInt(b.dataset.lbGoto)); }.bind(this));
      }, this);
      const submit = $('[data-lb-submit]', this.el);
      if (submit) submit.addEventListener('click', this.submit.bind(this));
    }

    // Background-load every color product so prices/images are ready instantly.
    async prefetchAll() {
      const handles = [];
      (this.cfg.models || []).forEach(function (m) { m.colorHandles.forEach(function (h) { if (handles.indexOf(h) === -1) handles.push(h); }); });
      const missing = handles.filter(function (h) { return !this.cache[h]; }, this);
      await Promise.all(missing.map(function (h) {
        return fetchProduct(h).then(function (p) { if (p) this.cache[h] = p; }.bind(this));
      }, this));
      // refresh anything currently shown
      if (this.s.modelIndex !== null) { this.updatePreview(); if (this.currentStep === 2) this.renderColors(); }
      this.renderModels();
    }

    // Load accessory product images (by handle) so each option shows a photo.
    async prefetchAccessoryImages() {
      const handles = (this.cfg.accessories || []).filter(function (a) { return a.handle && !a.image; }).map(function (a) { return a.handle; });
      const uniq = handles.filter(function (h, i) { return handles.indexOf(h) === i && !this.cache[h]; }, this);
      if (!uniq.length) { return; }
      await Promise.all(uniq.map(function (h) { return fetchProduct(h).then(function (p) { if (p) this.cache[h] = p; }.bind(this)); }, this));
      // fill in images on the accessory objects and re-render
      (this.cfg.accessories || []).forEach(function (a) {
        if (!a.image && a.handle && this.cache[a.handle]) a.image = this.productImage(this.cache[a.handle]);
      }, this);
      this.renderAccessories();
    }

    /* ---------- helpers on product data ---------- */
    modelProducts(mi) {
      const m = this.cfg.models[mi];
      if (!m) return [];
      return m.colorHandles.map(function (h) { return this.cache[h]; }, this).filter(Boolean);
    }
    colorLabel(model, product) {
      // strip the model name from the product title → "Grankotten Brun" → "Brun"
      const t = (product.title || '').trim();
      const name = (model.name || '').trim();
      if (name && t.toLowerCase().indexOf(name.toLowerCase()) === 0) return t.slice(name.length).trim() || t;
      return t;
    }
    productImage(product) {
      return (product && (product.featured_image || (product.images && product.images[0]))) || null;
    }
    variantImage(variant, product) {
      if (variant && variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
      return this.productImage(product);
    }
    modelMinPrice(mi) {
      const products = this.modelProducts(mi);
      let min = null;
      products.forEach(function (p) {
        (p.variants || []).forEach(function (v) { if (min === null || v.price < min) min = v.price; });
      });
      return min;
    }

    /* ---------- Step 1: Models ---------- */
    renderModels() {
      const wrap = $('[data-lb-models]', this.el);
      const models = this.cfg.models || [];
      if (!wrap) return;
      if (!models.length) { wrap.innerHTML = '<p class="lamp-builder__empty">Lägg till minst en modell i temaredigeraren.</p>'; return; }
      wrap.innerHTML = '';
      models.forEach(function (m, i) {
        const img = m.image || (this.modelProducts(i)[0] ? this.productImage(this.modelProducts(i)[0]) : null);
        const min = this.modelMinPrice(i);
        const priceHtml = min != null ? '<span class="lamp-builder__model-price">från ' + money(min) + '</span>' : '';
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'lamp-builder__model' + (this.s.modelIndex === i ? ' is-selected' : '');
        card.innerHTML =
          (img ? '<img class="lamp-builder__model-img" src="' + img + '" alt="' + esc(m.name) + '" loading="lazy" />'
               : '<div class="lamp-builder__model-img lamp-builder__model-img--ph">' + PH + '</div>') +
          '<div class="lamp-builder__model-body">' +
            '<span class="lamp-builder__model-name">' + esc(m.name) + '</span>' +
            (m.subtitle ? '<span class="lamp-builder__model-sub">' + esc(m.subtitle) + '</span>' : '') +
            priceHtml +
          '</div>' + CHECK;
        card.addEventListener('click', function () { this.selectModel(i); }.bind(this));
        wrap.appendChild(card);
      }, this);
    }

    async selectModel(i) {
      this.s.modelIndex = i;
      this.s.colorIndex = null; this.s.sizeIndex = null;
      this.renderModels(); // refresh selected state + relative prices
      this.enableNext(1, true);
      // Ensure this model's products are loaded (for price + colors)
      const m = this.cfg.models[i];
      const missing = m.colorHandles.filter(function (h) { return !this.cache[h]; }, this);
      if (missing.length) {
        await Promise.all(missing.map(function (h) { return fetchProduct(h).then(function (p) { if (p) this.cache[h] = p; }.bind(this)); }, this));
        this.renderModels();
      }
      this.updatePreview();
    }

    /* ---------- Step 2: Colors ---------- */
    renderColors() {
      const wrap = $('[data-lb-colors]', this.el);
      if (!wrap) return;
      const model = this.cfg.models[this.s.modelIndex];
      const products = this.modelProducts(this.s.modelIndex);
      wrap.innerHTML = '';
      if (!products.length) { wrap.innerHTML = '<p class="lamp-builder__empty">Kunde inte läsa färgprodukterna. Kontrollera handles i temaredigeraren.</p>'; return; }
      products.forEach(function (p, i) {
        const label = this.colorLabel(model, p);
        const hex = colorHex(label);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lamp-builder__swatch' + (this.s.colorIndex === i ? ' is-selected' : '');
        btn.title = label;
        btn.innerHTML =
          '<span class="lamp-builder__swatch-dot" style="background:' + hex + '"></span>' +
          '<span class="lamp-builder__swatch-label">' + esc(label) + '</span>';
        btn.addEventListener('click', function () { this.selectColor(i); }.bind(this));
        wrap.appendChild(btn);
      }, this);
      this.enableNext(2, this.s.colorIndex !== null);
    }

    selectColor(i) {
      this.s.colorIndex = i;
      this.s.sizeIndex = null; // reset size when color changes
      $$('[data-lb-colors] .lamp-builder__swatch', this.el).forEach(function (b, idx) { b.classList.toggle('is-selected', idx === i); });
      this.enableNext(2, true);
      this.updatePreview();
    }

    /* ---------- Step 3: Sizes ---------- */
    currentProduct() {
      const products = this.modelProducts(this.s.modelIndex);
      return this.s.colorIndex !== null ? products[this.s.colorIndex] : null;
    }
    renderSizes() {
      const wrap = $('[data-lb-sizes]', this.el);
      if (!wrap) return;
      const product = this.currentProduct();
      wrap.innerHTML = '';
      if (!product) { wrap.innerHTML = '<p class="lamp-builder__empty">Välj färg först.</p>'; return; }
      const model = this.cfg.models[this.s.modelIndex];
      const dims = parseDims(model && model.dimensions);
      const variants = product.variants || [];
      const selPrice = this.s.sizeIndex !== null && variants[this.s.sizeIndex] ? variants[this.s.sizeIndex].price : null;
      variants.forEach(function (v, i) {
        const sizeName = v.option1 || v.title;
        const dim = dims[(sizeName || '').toLowerCase()];
        // Before a choice: absolute prices. After: selected shows absolute,
        // the others show ± relative to the selected size (e.g. −1 000 kr).
        let priceHtml;
        if (selPrice == null || i === this.s.sizeIndex) {
          priceHtml = '<span class="lamp-builder__sizebtn-price">' + money(v.price) + '</span>';
        } else {
          const diff = v.price - selPrice;
          const cls = diff < 0 ? 'lamp-builder__sizebtn-price--minus' : 'lamp-builder__sizebtn-price--plus';
          priceHtml = '<span class="lamp-builder__sizebtn-price ' + cls + '">' + signedMoney(diff) + '</span>';
        }
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lamp-builder__sizebtn' + (this.s.sizeIndex === i ? ' is-selected' : '') + (v.available ? '' : ' is-unavailable');
        btn.disabled = !v.available;
        btn.innerHTML =
          '<span class="lamp-builder__sizebtn-main">' +
            '<span class="lamp-builder__sizebtn-name">' + esc(sizeName) + '</span>' +
            (dim ? '<span class="lamp-builder__sizebtn-dim">' + esc(dim) + '</span>' : '') +
            (v.available ? '' : '<span class="lamp-builder__sizebtn-out">Slut i lager</span>') +
          '</span>' +
          priceHtml + CHECK;
        if (v.available) btn.addEventListener('click', function () { this.selectSize(i); }.bind(this));
        wrap.appendChild(btn);
      }, this);
      this.enableNext(3, this.s.sizeIndex !== null);
    }

    selectSize(i) {
      this.s.sizeIndex = i;
      this.renderSizes(); // refresh selected state + relative prices
      this.enableNext(3, true);
      this.updatePreview();
    }

    currentVariant() {
      const p = this.currentProduct();
      if (!p || this.s.sizeIndex === null) return null;
      return p.variants[this.s.sizeIndex] || null;
    }

    /* ---------- Step 4: Accessories ---------- */
    renderAccessories() {
      ['cable', 'bulb', 'socket'].forEach(function (group) {
        const wrap = $('[data-lb-addon="' + group + '"]', this.el);
        const groupEl = $('[data-lb-addon-group="' + group + '"]', this.el);
        const items = this.groups[group] || [];
        if (!wrap) return;
        if (!items.length) { if (groupEl) groupEl.hidden = true; return; }
        if (groupEl) groupEl.hidden = false;
        wrap.innerHTML = '';
        items.forEach(function (item, idx) {
          const isEco = !item.id;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'lamp-builder__option' + (this.s[group] === idx ? ' is-selected' : '');
          let media;
          if (item.image) media = '<img class="lamp-builder__option-media" src="' + item.image + '" alt="" loading="lazy" />';
          else if (isEco) media = '<span class="lamp-builder__option-media lamp-builder__option-media--leaf"><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z"/></svg></span>';
          else media = '<span class="lamp-builder__option-media lamp-builder__option-media--ph">' + PH + '</span>';
          btn.innerHTML = media +
            '<span class="lamp-builder__option-text">' +
              '<span class="lamp-builder__option-label">' + esc(item.label) + '</span>' +
              (item.info ? '<span class="lamp-builder__option-info">' + esc(item.info) + '</span>' : '') +
              '<span class="lamp-builder__option-meta ' + (isEco ? 'lamp-builder__option-eco' : '') + '">' + (isEco ? 'Miljövänligt val' : 'Ingår') + '</span>' +
            '</span>' + CHECK;
          btn.addEventListener('click', function () { this.selectAccessory(group, idx); }.bind(this));
          wrap.appendChild(btn);
        }, this);
      }, this);
    }
    selectAccessory(group, idx) {
      this.s[group] = idx;
      $$('[data-lb-addon="' + group + '"] .lamp-builder__option', this.el).forEach(function (b, i) { b.classList.toggle('is-selected', i === idx); });
      // show chosen value on the header
      const valEl = $('[data-lb-acc-value="' + group + '"]', this.el);
      if (valEl) { valEl.textContent = this.groups[group][idx].label; valEl.classList.add('is-chosen'); }
      const accEl = $('[data-lb-acc="' + group + '"]', this.el);
      if (accEl) accEl.classList.add('is-done');
      // close this group, open the next one that still needs a choice
      const order = ['cable', 'bulb', 'socket'].filter(function (g) { return (this.groups[g] || []).length; }, this);
      const next = order.find(function (g) { return this.s[g] === null; }, this);
      this.openAccGroup(next || null);
      this.updateAddonsNext();
    }

    setupAccordions() {
      ['cable', 'bulb', 'socket'].forEach(function (group) {
        const head = $('[data-lb-acc-head="' + group + '"]', this.el);
        const accEl = $('[data-lb-acc="' + group + '"]', this.el);
        if (!head || !accEl) return;
        if (!(this.groups[group] || []).length) { accEl.hidden = true; return; }
        head.addEventListener('click', function () {
          this.openAccGroup(this.openGroup === group ? null : group);
        }.bind(this));
      }, this);
      this.openAccGroup('cable');
      this.updateAddonsNext();
    }

    openAccGroup(group) {
      this.openGroup = group;
      ['cable', 'bulb', 'socket'].forEach(function (g) {
        const accEl = $('[data-lb-acc="' + g + '"]', this.el);
        const head = $('[data-lb-acc-head="' + g + '"]', this.el);
        if (!accEl) return;
        const open = g === group;
        accEl.classList.toggle('is-open', open);
        if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
      }, this);
    }

    updateAddonsNext() {
      const order = ['cable', 'bulb', 'socket'].filter(function (g) { return (this.groups[g] || []).length; }, this);
      const allChosen = order.every(function (g) { return this.s[g] !== null; }, this);
      this.enableNext(4, allChosen);
    }

    setupQuantity() {
      const dec = $('[data-lb-qty-dec]', this.el);
      const inc = $('[data-lb-qty-inc]', this.el);
      const count = $('[data-lb-qty-count]', this.el);
      if (!dec || !inc || !count) return;
      dec.addEventListener('click', function () { if (this.s.quantity > 1) { this.s.quantity--; count.textContent = this.s.quantity; this.renderSummary(); } }.bind(this));
      inc.addEventListener('click', function () { if (this.s.quantity < 99) { this.s.quantity++; count.textContent = this.s.quantity; this.renderSummary(); } }.bind(this));
    }

    setupExtras() {
      const extrasWrap = $('[data-lb-extras]', this.el);
      let any = false;
      if (this.cfg.engravingEnabled && this.cfg.engravingId) {
        any = true;
        const blk = $('[data-lb-engraving-block]', this.el); blk.hidden = false;
        $('[data-lb-engraving-price]', this.el).textContent = '+' + money(this.cfg.engravingPrice);
        if (this.cfg.engravingImage) { const t = $('[data-lb-engraving-thumb]', this.el); t.innerHTML = '<img src="' + this.cfg.engravingImage + '" alt="" />'; t.classList.add('has-image'); }
        const tog = $('[data-lb-engraving-toggle]', this.el), field = $('[data-lb-engraving-field]', this.el), text = $('[data-lb-engraving-text]', this.el), count = $('[data-lb-engraving-counter]', this.el);
        tog.addEventListener('change', function () {
          this.s.engraving = tog.checked; field.hidden = !tog.checked;
          if (!tog.checked) { text.value = ''; count.textContent = '0'; this.s.engravingText = ''; } else setTimeout(function () { text.focus(); }, 50);
        }.bind(this));
        text.addEventListener('input', function () { count.textContent = text.value.length; this.s.engravingText = text.value.trim(); }.bind(this));
      }
      if (this.cfg.treeEnabled && this.cfg.treeId) {
        any = true;
        const blk = $('[data-lb-tree-block]', this.el); blk.hidden = false;
        $('[data-lb-tree-price]', this.el).textContent = '+' + money(this.cfg.treePrice) + ' / träd';
        const count = $('[data-lb-tree-count]', this.el);
        $('[data-lb-tree-decrement]', this.el).addEventListener('click', function () { if (this.s.trees > 0) { this.s.trees--; count.textContent = this.s.trees; } }.bind(this));
        $('[data-lb-tree-increment]', this.el).addEventListener('click', function () { if (this.s.trees < 99) { this.s.trees++; count.textContent = this.s.trees; } }.bind(this));
      }
      if (extrasWrap) extrasWrap.hidden = !any;
    }

    setupDelivery() {
      const wrap = $('[data-lb-delivery]', this.el);
      if (!wrap || !this.cfg.deliveryEnabled) { if (wrap) wrap.hidden = true; return; }
      const toggle = $('[data-lb-delivery-toggle]', this.el);
      const panel = $('[data-lb-delivery-panel]', this.el);
      const input = $('[data-lb-delivery-input]', this.el);
      const valueEl = $('[data-lb-delivery-value]', this.el);
      const clearBtn = $('[data-lb-delivery-clear]', this.el);

      // earliest selectable = 10 working days from today (weekends skipped)
      const min = new Date();
      let added = 0;
      while (added < 10) { min.setDate(min.getDate() + 1); const d = min.getDay(); if (d !== 0 && d !== 6) added++; }
      const iso = min.toISOString().slice(0, 10);
      input.min = iso;

      toggle.addEventListener('click', function () {
        const open = panel.hasAttribute('hidden');
        if (open) { panel.removeAttribute('hidden'); toggle.setAttribute('aria-expanded', 'true'); wrap.classList.add('is-open'); }
        else { panel.setAttribute('hidden', ''); toggle.setAttribute('aria-expanded', 'false'); wrap.classList.remove('is-open'); }
      });

      const fmt = function (isoStr) {
        try {
          const d = new Date(isoStr + 'T00:00:00');
          return d.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        } catch (e) { return isoStr; }
      };

      input.addEventListener('change', function () {
        this.s.deliveryDate = input.value || '';
        if (input.value) {
          valueEl.textContent = fmt(input.value);
          valueEl.classList.add('is-set');
          clearBtn.hidden = false;
        } else {
          valueEl.textContent = 'Standard – skickas inom 10 arbetsdagar';
          valueEl.classList.remove('is-set');
          clearBtn.hidden = true;
        }
      }.bind(this));

      clearBtn.addEventListener('click', function () {
        input.value = ''; this.s.deliveryDate = '';
        valueEl.textContent = 'Standard – skickas inom 10 arbetsdagar';
        valueEl.classList.remove('is-set');
        clearBtn.hidden = true;
      }.bind(this));
    }

    deliveryDisplay() {
      if (!this.s.deliveryDate) return null;
      try { return new Date(this.s.deliveryDate + 'T00:00:00').toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' }); }
      catch (e) { return this.s.deliveryDate; }
    }

    /* ---------- Live preview (image + specs + price) ---------- */
    updatePreview() {
      const media = $('[data-lb-preview-media]', this.el);
      const specs = $('[data-lb-preview-specs]', this.el);
      const priceEl = $('[data-lb-price]', this.el);
      if (!media) return;

      const model = this.s.modelIndex !== null ? this.cfg.models[this.s.modelIndex] : null;
      const product = this.currentProduct();
      const variant = this.currentVariant();

      // Image priority: variant → color product → model image →
      // model's first color-product image → default image (only before a model is picked)
      let img = null;
      if (variant) img = this.variantImage(variant, product);
      else if (product) img = this.productImage(product);
      else if (this.s.modelIndex !== null) {
        img = (model && model.image) || null;
        if (!img) { const fp = this.modelProducts(this.s.modelIndex)[0]; if (fp) img = this.productImage(fp); }
      }
      if (!img) img = this.cfg.defaultImage || null;

      if (img) { media.innerHTML = '<img src="' + img + '" alt="" class="lamp-builder__preview-img" />'; media.classList.add('has-image'); }
      else { media.innerHTML = '<div class="lamp-builder__preview-ph">' + PH.replace('width="24" height="24"', 'width="46" height="46"') + '</div>'; media.classList.remove('has-image'); }

      // Specs
      const rows = [];
      if (model) rows.push(['Modell', model.name]);
      if (product) rows.push(['Färg', this.colorLabel(model, product)]);
      if (variant) rows.push(['Storlek', variant.option1 || variant.title]);
      if (specs) {
        specs.innerHTML = rows.length
          ? rows.map(function (r) { return '<div class="lamp-builder__preview-spec"><span>' + esc(r[0]) + '</span><strong>' + esc(r[1]) + '</strong></div>'; }).join('')
          : '<p class="lamp-builder__preview-hint">Dina val visas här allt eftersom.</p>';
      }

      // Price: exact variant price if size chosen, otherwise "från" min price of the model
      if (priceEl) {
        if (variant) priceEl.innerHTML = '<span class="lamp-builder__price-amount">' + money(variant.price) + '</span>';
        else if (model !== null && this.s.modelIndex !== null) {
          const min = this.modelMinPrice(this.s.modelIndex);
          priceEl.innerHTML = min != null ? '<span class="lamp-builder__price-from">från</span> <span class="lamp-builder__price-amount">' + money(min) + '</span>' : '';
        } else priceEl.innerHTML = '';
      }
    }

    /* ---------- Step 5: Summary ---------- */
    renderSummary() {
      const wrap = $('[data-lb-summary]', this.el);
      if (!wrap) return;
      const model = this.cfg.models[this.s.modelIndex];
      const product = this.currentProduct();
      const variant = this.currentVariant();
      const rows = [];
      let total = 0;

      const lampPrice = variant ? variant.price : (this.modelMinPrice(this.s.modelIndex) || 0);
      total += lampPrice;
      const spec = [];
      if (product) spec.push(this.colorLabel(model, product));
      if (variant) spec.push(variant.option1 || variant.title);
      const img = variant ? this.variantImage(variant, product) : (product ? this.productImage(product) : (model && model.image));

      rows.push('<div class="lamp-builder__srow lamp-builder__srow--lamp">' +
        (img ? '<img class="lamp-builder__srow-img" src="' + img + '" alt="" />' : '') +
        '<div class="lamp-builder__srow-body"><p class="lamp-builder__srow-cat">Lampa</p>' +
        '<p class="lamp-builder__srow-val">' + esc(model ? model.name : '') + '</p>' +
        (spec.length ? '<p class="lamp-builder__srow-extra">' + esc(spec.join(' · ')) + '</p>' : '') + '</div>' +
        '<span class="lamp-builder__srow-price">' + money(lampPrice) + '</span></div>');

      const accRow = function (group, cat) {
        const idx = this.s[group];
        if (idx === null) return;
        const item = this.groups[group][idx];
        const im = item.image ? '<img class="lamp-builder__srow-img" src="' + item.image + '" alt="" />' : '';
        rows.push('<div class="lamp-builder__srow">' + im +
          '<div class="lamp-builder__srow-body"><p class="lamp-builder__srow-cat">' + cat + '</p>' +
          '<p class="lamp-builder__srow-val">' + esc(item.label) + '</p></div>' +
          '<span class="lamp-builder__srow-price lamp-builder__srow-price--free">Ingår</span></div>');
      }.bind(this);
      accRow('cable', 'Sladd'); accRow('bulb', 'Ljuskälla'); accRow('socket', 'Takkontakt');

      if (this.s.engraving && this.s.engravingText) {
        const price = this.cfg.engravingPrice; total += price;
        const im = this.cfg.engravingImage ? '<img class="lamp-builder__srow-img" src="' + this.cfg.engravingImage + '" alt="" />' : '';
        rows.push('<div class="lamp-builder__srow">' + im +
          '<div class="lamp-builder__srow-body"><p class="lamp-builder__srow-cat">Personlig skylt</p>' +
          '<p class="lamp-builder__srow-val">Graverad träskylt</p>' +
          '<p class="lamp-builder__srow-extra">”' + esc(this.s.engravingText) + '”</p></div>' +
          '<span class="lamp-builder__srow-price">' + money(price) + '</span></div>');
      }
      if (this.s.trees > 0) {
        const price = this.cfg.treePrice * this.s.trees; total += price;
        rows.push('<div class="lamp-builder__srow"><div class="lamp-builder__srow-body">' +
          '<p class="lamp-builder__srow-cat">Miljöbidrag</p><p class="lamp-builder__srow-val">Plantera ' + this.s.trees + ' träd</p></div>' +
          '<span class="lamp-builder__srow-price">' + money(price) + '</span></div>');
      }

      const qty = this.s.quantity || 1;
      const grand = total * qty;
      rows.push('<div class="lamp-builder__stotal">' +
        '<span>Totalt' + (qty > 1 ? ' (' + qty + ' st)' : '') + '</span>' +
        '<span class="lamp-builder__stotal-amount">' + money(grand) + '</span></div>');

      // Delivery date (or standard)
      if (this.cfg.deliveryEnabled) {
        const disp = this.deliveryDisplay();
        rows.push('<div class="lamp-builder__srow lamp-builder__srow--delivery">' +
          '<div class="lamp-builder__srow-body"><p class="lamp-builder__srow-cat">' + esc(this.cfg.deliveryLabel || 'Leverans') + '</p>' +
          '<p class="lamp-builder__srow-val">' + (disp ? esc(disp) : 'Standard – skickas inom 10 arbetsdagar') + '</p></div></div>');
      }

      wrap.innerHTML = rows.join('');
    }

    /* ---------- Navigation ---------- */
    goTo(step) {
      this.currentStep = step;
      this.el.dataset.step = String(step);
      $$('[data-lb-step]', this.el).forEach(function (s) { s.classList.toggle('is-active', toInt(s.dataset.lbStep) === step); });
      $$('[data-lb-goto]', this.el).forEach(function (b) {
        const s = toInt(b.dataset.lbGoto);
        b.classList.remove('is-active', 'is-complete');
        if (s < step) b.classList.add('is-complete'); else if (s === step) b.classList.add('is-active');
        if (s <= step) b.disabled = false;
      });
      if (step === 2) this.renderColors();
      if (step === 3) this.renderSizes();
      if (step === 5) this.renderSummary();
      if (typeof this.el.scrollIntoView === 'function') this.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    next() { if (this.currentStep < 5) this.goTo(this.currentStep + 1); }
    prev() { if (this.currentStep > 1) this.goTo(this.currentStep - 1); }
    enableNext(step, on) {
      const stepEl = $('[data-lb-step="' + step + '"]', this.el);
      if (!stepEl) return;
      const btn = $('[data-lb-next]', stepEl);
      if (btn) btn.disabled = !on;
    }

    /* ---------- Submit ---------- */
    async submit() {
      const variant = this.currentVariant();
      if (!variant) { alert('Välj modell, färg och storlek innan du lägger till i varukorgen.'); return; }
      const btn = $('[data-lb-submit]', this.el), label = $('[data-lb-submit-label]', this.el), spinner = $('[data-lb-submit-spinner]', this.el);
      btn.disabled = true; label.textContent = 'Lägger till…'; spinner.hidden = false;

      const qty = this.s.quantity || 1;
      const bundleId = 'lamp-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
      const mainProps = { '_bundle_id': bundleId, '_bundle_role': 'main' };
      if (this.s.deliveryDate) mainProps[(this.cfg.deliveryLabel || 'Önskat leveransdatum')] = this.deliveryDisplay();
      const items = [{ id: variant.id, quantity: qty, properties: mainProps }];

      const addAddon = function (id, lbl, extra) {
        const p = { '_bundle_id': bundleId, '_bundle_role': 'addon', '_addon_label': lbl };
        if (extra) Object.assign(p, extra);
        items.push({ id: toInt(id), quantity: qty, properties: p });
      };
      ['cable', 'bulb', 'socket'].forEach(function (g) {
        const idx = this.s[g]; if (idx === null) return;
        const item = this.groups[g][idx];
        if (item.id) addAddon(item.id, ({ cable: 'Sladd', bulb: 'Ljuskälla', socket: 'Takkontakt' }[g]) + ': ' + item.label);
      }, this);
      if (this.s.engraving && this.s.engravingText) addAddon(this.cfg.engravingId, 'Personlig graverad skylt', { 'Gravyrtext': this.s.engravingText });
      if (this.s.trees > 0) items.push({ id: toInt(this.cfg.treeId), quantity: this.s.trees * qty, properties: { '_bundle_id': bundleId, '_bundle_role': 'addon', '_addon_label': 'Plantera träd' } });

      try {
        const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: items }) });
        if (!res.ok) { const e = await res.json().catch(function () { return null; }); throw new Error((e && e.description) || 'Något gick fel.'); }
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(function () { const d = document.querySelector('cart-drawer'); if (d && typeof d.show === 'function') d.show(); }, 300);
        label.textContent = 'Tillagd ✓';
        setTimeout(function () { btn.disabled = false; label.textContent = 'Lägg till i varukorg'; spinner.hidden = true; }, 1500);
      } catch (err) {
        console.error('[lamp-builder] submit failed:', err);
        alert('Något gick fel: ' + err.message + '\n\nPröva igen eller kontakta oss om problemet kvarstår.');
        btn.disabled = false; label.textContent = 'Lägg till i varukorg'; spinner.hidden = true;
      }
    }
  }

  if (!customElements.get('lamp-builder')) {
    class LampBuilderElement extends HTMLElement {
      connectedCallback() { if (this._init) return; this._init = true; new LampBuilder(this); }
    }
    customElements.define('lamp-builder', LampBuilderElement);
  }
})();
